import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { MobileBookingBar } from "@/components/site/MobileBookingBar";
import { ActivitiesHero } from "@/components/site/activities/ActivitiesHero";
import { ActivitiesIntro } from "@/components/site/activities/ActivitiesIntro";
import { ExperienceDirectory } from "@/components/site/activities/ExperienceDirectory";
import { EventsGroupsSection } from "@/components/site/activities/EventsGroupsSection";
import { LifeAtAmigo } from "@/components/site/activities/LifeAtAmigo";
import { OnSiteRecreation } from "@/components/site/activities/OnSiteRecreation";
import { FalconZapataSection } from "@/components/site/activities/FalconZapataSection";
import { ActivitiesGallery } from "@/components/site/activities/ActivitiesGallery";
import { GroupPlanning } from "@/components/site/activities/GroupPlanning";
import { ActivitiesCTA } from "@/components/site/activities/ActivitiesCTA";
import { ACTIVITIES_PATH, BUSINESS, CAMPGROUND_ID, FACEBOOK_URL } from "@/lib/site";
import { ACTIVITIES_HERO } from "@/components/site/activities/galleryData";

const TITLE = "Activities & Group Stays | Amigo Inn RV Park";
const DESCRIPTION =
  "Discover fishing, boating, guest gatherings, family celebrations, recreation and group stays at Amigo Inn & RV Park near Falcon Lake in Zapata, Texas.";
const CANONICAL = `https://amigoinn-rvpark.com${ACTIVITIES_PATH}`;
const OG_IMAGE = `https://amigoinn-rvpark.com${ACTIVITIES_HERO.src}`;

export const Route = createFileRoute("/activities")({
  component: ActivitiesPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      {
        rel: "preload",
        as: "image",
        href: ACTIVITIES_HERO.src,
        imageSrcSet: ACTIVITIES_HERO.srcSet,
        fetchPriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: TITLE,
          url: CANONICAL,
          description: DESCRIPTION,
          image: OG_IMAGE,
          isPartOf: {
            "@type": "WebSite",
            name: BUSINESS.name,
            url: BUSINESS.siteUrl,
          },
          about: { "@id": CAMPGROUND_ID },
          mainEntity: {
            "@id": CAMPGROUND_ID,
            "@type": ["Campground", "LodgingBusiness"],
            name: BUSINESS.name,
            url: BUSINESS.siteUrl,
            telephone: BUSINESS.phone,
            email: BUSINESS.email,
            sameAs: [FACEBOOK_URL],
            address: {
              "@type": "PostalAddress",
              streetAddress: BUSINESS.street,
              addressLocality: BUSINESS.city,
              addressRegion: BUSINESS.region,
              postalCode: BUSINESS.postalCode,
              addressCountry: BUSINESS.country,
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: BUSINESS.siteUrl,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Activities",
              item: CANONICAL,
            },
          ],
        }),
      },
    ],
  }),
});

function ActivitiesPage() {
  return (
    <div className="min-h-dvh bg-background">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-sm focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-forest"
      >
        Skip to content
      </a>

      <SiteHeader />

      <main id="main">
        <ActivitiesHero />
        <ActivitiesIntro />
        <ExperienceDirectory />
        <EventsGroupsSection />
        <LifeAtAmigo />
        <OnSiteRecreation />
        <FalconZapataSection />
        <ActivitiesGallery />
        <GroupPlanning />
        <ActivitiesCTA />
      </main>

      <SiteFooter
        newsletterHeading="Stay Connected With Amigo Inn"
        newsletterDescription="Receive park news, seasonal information, event announcements, and updates from Amigo Inn & RV Park."
      />
      <MobileBookingBar />
    </div>
  );
}
